import { ListKeyManager } from "@angular/cdk/a11y";
import { DOWN_ARROW, ENTER, SHIFT, TAB, UP_ARROW } from "@angular/cdk/keycodes";
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ContentChild,
	ElementRef,
	EventEmitter,
	forwardRef,
	HostBinding,
	HostListener,
	inject,
	Input,
	NgZone,
	Output,
	QueryList,
	TemplateRef,
	ViewChild,
	ViewChildren,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

import { KeyboardService } from "../../services/keyboard.service";

const INPUT_CONTROL_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => SelectCellComponent),
	multi: true,
};

@Component({
	selector: "ic-select-input-cell",
	styleUrls: ["select-cell.component.scss"],
	providers: [INPUT_CONTROL_ACCESSOR],
	templateUrl: "./select-cell.component.html",
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class SelectCellComponent implements ControlValueAccessor, AfterViewInit {
	private cdRef = inject(ChangeDetectorRef);
	private eRef = inject(ElementRef);
	private ngZone = inject(NgZone);
	private keyBoarService = inject(KeyboardService);

	keyboardEventsManager: ListKeyManager<any>;
	value: any;
	itemIndex = 0;
	showList = false;
	disabled = false;

	private blurredFromGoingUp = false;
	private listener = this.onClick.bind(this);

	@Input() key: string;
	@Input() errorText = "";
	@Input() items: readonly unknown[] = [];
	@Input() placeholder = "";

	@Output() blurEvent = new EventEmitter();

	@ContentChild("itemTmpl", { static: false }) itemTemplate: TemplateRef<any>;
	@ContentChild("emptyTmpl", { static: false }) emptyTemplate: TemplateRef<any>;
	@ContentChild("labelTmpl", { static: false }) labelTemplate: TemplateRef<any>;

	// Manage keyboard events
	@ViewChildren("listItem") listItems: QueryList<any>;

	@ViewChild("list", { static: false }) list: ElementRef;
	@ViewChild("input", { static: true }) input: ElementRef;

	@HostBinding("attr.tabIndex") tabIndex = 0;
	@HostBinding("class.error") @Input() error = false;

	@HostListener("focus")
	onFocus() {
		if (this.showList) {
			this.eRef.nativeElement.blur();
		}

		if (this.blurredFromGoingUp) {
			return;
		}

		this.toggleList();
	}

	@HostListener("blur")
	onBlurred() {
		this.blurredFromGoingUp = false;
	}

	@HostListener("click")
	onClicked() {
		if (!this.blurredFromGoingUp) {
			return;
		}

		this.blurredFromGoingUp = false;
		this.toggleList();
	}

	@HostListener("keydown", ["$event"])
	onKeydown(event: KeyboardEvent) {
		if (this.showList || event.keyCode !== TAB || this.keyBoarService.isDown(SHIFT)) {
			return;
		}

		this.toggleList();
		this.navigateToFirstItem();

		return false;
	}

	private onTouch = () => {}; // eslint-disable-line @typescript-eslint/no-empty-function
	private onModelChange = (_: unknown) => {};

	writeValue(value: any) {
		this.value = value;
		this.cdRef.markForCheck();
	}

	registerOnChange(fn: (v: unknown) => void) {
		this.onModelChange = fn;
	}

	registerOnTouched(fn: () => void) {
		this.onTouch = fn;
	}

	setDisabledState(isDisabled: boolean) {
		this.disabled = isDisabled;
		this.tabIndex = this.disabled ? -1 : 0;
		this.cdRef.markForCheck();
	}

	toggleList() {
		if (this.disabled) {
			return;
		}

		this.showList = !this.showList;

		if (this.showList) {
			this.listenToClick();
			this.navigateToFirstItem();
		} else {
			this.removeClick();
			this.itemIndex = -1;
		}
	}

	onChange(value: any) {
		this.onModelChange(value);
	}

	selectItem(item: unknown) {
		this.value = this.key ? item[this.key] : item;
		this.onTouch();
		this.onChange(this.value);
		this.blurInput();
		this.cdRef.markForCheck();
	}

	focus() {
		this.eRef.nativeElement.focus();
	}

	blur(index: number) {
		const shiftDown = this.keyBoarService.isDown(SHIFT);
		const tabbing = this.keyBoarService.isDown(TAB);
		if (tabbing && ((index === 0 && shiftDown) || (index === this.items.length - 1 && !shiftDown))) {
			this.blurredFromGoingUp = index === 0 && shiftDown;
			this.blurInput();
		}

		return;
	}

	blurInput() {
		this.itemIndex = 0;
		this.onTouch();
		this.toggleList();
		this.blurEvent.emit();
	}

	navigateToFirstItem() {
		this.cdRef.detectChanges();
		const item = this.listItems.toArray()[0];

		if (!item) {
			return;
		}

		this.eRef.nativeElement.blur();
		item.nativeElement.focus();
	}

	handleKeyUp(event: KeyboardEvent, item?: any) {
		if (!this.showList) {
			return;
		}

		if (event.keyCode === DOWN_ARROW || event.keyCode === UP_ARROW) {
			this.navigate(event);
		} else if (event.keyCode === ENTER) {
			this.selectItem(item);
		}
	}

	setItemIndex(index: number) {
		this.itemIndex = index;
	}

	ngAfterViewInit() {
		this.keyboardEventsManager = new ListKeyManager(this.listItems);
	}

	private navigate(event: KeyboardEvent) {
		const isDown = event.keyCode === DOWN_ARROW;
		const newIndex = isDown ? this.itemIndex + 1 : this.itemIndex - 1;
		const items = this.listItems.toArray();

		if (newIndex < 0 || newIndex > items.length - 1) {
			return false;
		}

		const current = items[newIndex];
		const previous = isDown ? items[newIndex - 1] : items[newIndex + 1];

		if (current) {
			current.nativeElement.focus();
			this.list.nativeElement.scrollTo(0, current.nativeElement.clientHeight * this.itemIndex);
		}

		if (previous) {
			previous.nativeElement.blur();
		}
	}

	private listenToClick() {
		this.ngZone.runOutsideAngular(() => {
			window.addEventListener("click", this.listener);
		});
	}

	private removeClick() {
		window.removeEventListener("click", this.listener);
	}

	private onClick(event: MouseEvent) {
		event.preventDefault();

		if (this.eRef.nativeElement.contains(event.target)) {
			return;
		}

		this.ngZone.run(() => {
			this.showList = false;
			this.onTouch();
			this.cdRef.markForCheck();
		});

		this.removeClick();
	}
}
